export function pickRandomReports(reports, count, random = Math.random) {
  const unique = new Map();
  if (Array.isArray(reports)) reports.forEach((report) => {
    if (report?.title && report?.url && isReportPhoto(report.image) && !unique.has(report.url)) unique.set(report.url, report);
  });
  const pool = [...unique.values()];
  for (let index = pool.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [pool[index], pool[swap]] = [pool[swap], pool[index]];
  }
  return pool.slice(0, Math.max(0, count));
}

export function pickNewestReports(reports, count) {
  const unique = new Map();
  for (const report of Array.isArray(reports) ? reports : []) {
    if (report?.title && report?.url && isReportPhoto(report.image) && !unique.has(report.url)) unique.set(report.url, report);
  }
  return [...unique.values()].sort((a, b) => {
    const left = Date.parse(a.published_at);
    const right = Date.parse(b.published_at);
    if (Number.isFinite(left) !== Number.isFinite(right)) return Number.isFinite(left) ? -1 : 1;
    return (Number.isFinite(left) ? right - left : 0) || a.url.localeCompare(b.url);
  }).slice(0, Math.max(0, count));
}

export function isReportPhoto(value) {
  const source = typeof value === 'string' ? value : value?.src;
  return Boolean(source) && !/(?:^|\/\/)(?:i\.ytimg\.com|img\.youtube\.com|[^/]*youtube\.com)\//i.test(source)
    && !/urb-report-youtube-|youtube[_:-]|video thumbnail/i.test(`${source} ${value?.role || ''} ${value?.logical_id || ''} ${value?.alt || ''}`);
}

export function reportOpeningText(html, title = '') {
  if (typeof html !== 'string') return '';
  const content = html.replace(/<!--[\s\S]*?(?:-->|$)/g, '')
    .replace(/<(script|style|figcaption|h[1-6])\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/gi, '');
  const paragraphs = [...content.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p\s*>/gi)];
  const candidates = paragraphs.length ? paragraphs.map((match) => match[1]) : [content];
  const label = (value) => value.replace(/&amp;/g, '&').replace(/:/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  const headings = new Set(['history', 'location history', 'our visit', 'the visit', 'the explore', 'the report', 'visit', 'report', 'exploration', 'background', 'overview']);
  for (const candidate of candidates) {
    const text = candidate.replace(/<\/?(?:p|div|li|br)\b[^>]*>/gi, ' ')
      .replace(/<[^>]*>/g, '').replace(/&(?:nbsp|#160|#x0*a0);/gi, ' ').replace(/\s+/g, ' ').trim();
    const dateProbe = text.toLowerCase().replace(/january|february|march|april|june|july|august|september|october|november|december|jan|feb|mar|apr|may|jun|jul|aug|sept|sep|oct|nov|dec/g, '')
      .replace(/[\d\s/.,-]/g, '');
    if (text && label(text) !== label(title) && !headings.has(label(text)) && !(dateProbe === '' && /\d/.test(text))) return text.slice(0, 600);
  }
  return '';
}

export function reportTeaser(value, maximum = 120) {
  const entities = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
    ndash: '–', mdash: '—', hellip: '…', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', pound: '£' };
  const text = String(value || '').replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (entity, key) => {
    if (key[0] !== '#') return entities[key.toLowerCase()] ?? entity;
    const point = Number.parseInt(key.slice(/^#x/i.test(key) ? 2 : 1), /^#x/i.test(key) ? 16 : 10);
    return point > 0 && point <= 0x10ffff && !(point >= 0xd800 && point <= 0xdfff) ? String.fromCodePoint(point) : '�';
  }).replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const budget = Math.max(4, maximum) - 3;
  let opening = text;
  if (opening.length > budget) {
    opening = opening.slice(0, budget + 1);
    const boundary = opening.lastIndexOf(' ');
    opening = boundary > 0 ? opening.slice(0, boundary) : opening.slice(0, budget);
  }
  return `${opening.replace(/[\s.,;:!?…]+$/u, '')}...`;
}

function parsedImages(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function normalizeHomepageReport(report) {
  const images = parsedImages(report?.display_images).filter(isReportPhoto);
  const image = images[0] || (report?.report_format !== 'Video report' && isReportPhoto(report.image) ? report.image : null);
  if (!report?.title || !report?.url || !image?.src) return null;
  return {
    title: report.title,
    url: report.url,
    image: image.src,
    imageAlt: image.alt || report.title,
    country: report.country || '',
    type: report.location_type || 'FIELD REPORT',
    published_at: report.published_at || '',
    opening_text: report.opening_text || '',
    date: Number.isFinite(Date.parse(report.published_at))
      ? new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' }).format(new Date(report.published_at)).toUpperCase()
      : 'ARCHIVE',
  };
}

export async function loadHomepageReportPool(indexUrl, fetchImpl = globalThis.fetch) {
  if (!indexUrl || typeof fetchImpl !== 'function') return { total: null, reports: [] };
  const fetchPage = async (page) => {
    const url = new URL(indexUrl, window.location.origin);
    url.searchParams.set('page', String(page));
    const response = await fetchImpl(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Reports feed returned ${response.status}`);
    return response.json();
  };
  const first = await fetchPage(1);
  if (!Array.isArray(first?.reports)) throw new Error('Reports feed contract mismatch');
  const total = Number(first.total);
  if (!Number.isSafeInteger(total) || total < 0) throw new Error('Reports feed total mismatch');
  const pageCount = first.version === 2 ? Math.max(1, Number(first.pages) || 1) : 1;
  const remaining = pageCount > 1
    ? await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2)))
    : [];
  if (remaining.some((page) => Number(page?.total) !== total || !Array.isArray(page?.reports))) {
    throw new Error('Reports feed pagination mismatch');
  }
  return {
    total,
    countries: [...new Set([first, ...remaining].flatMap(page => page.reports)
      .flatMap(report => String(report.country || '').split(/\s*\/\s*/)).filter(Boolean))],
    reports: [first, ...remaining]
      .flatMap((page) => page.reports)
      .map(normalizeHomepageReport)
      .filter(Boolean),
  };
}

function renderReportCount(scope, total) {
  if (!Number.isSafeInteger(total) || total < 0) return;
  const formatted = new Intl.NumberFormat('en-GB').format(total);
  scope.querySelectorAll('[data-home-report-count]').forEach((element) => {
    element.textContent = element.dataset?.homeReportTemplate
      ? element.dataset.homeReportTemplate.replace('{count}', formatted)
      : element.textContent.replace(/\d[\d,]*/, formatted);
  });
}

function setText(scope, selector, value) {
  const element = scope.querySelector(selector);
  if (element && value) element.textContent = value;
}

function setImage(scope, selector, report) {
  const image = scope.querySelector(selector);
  if (!image) return;
  image.src = report.image;
  image.removeAttribute?.('srcset');
  image.alt = report.imageAlt || report.title;
}

function renderEvidenceCard(card, report) {
  card.hidden = false;
  setImage(card, '[data-home-report-image]', report);
  setText(card, '[data-home-report-type]', report.type || 'FIELD REPORT');
  setText(card, '[data-home-report-title]', report.title);
  setText(card, '[data-home-report-country], .evidence-card__meta-row span:first-child', report.country || 'LOCATION UNCLASSIFIED');
  setText(card, '[data-home-report-date], .evidence-card__meta-row span:last-child', report.date || 'ARCHIVE');
  const summary = card.querySelector('[data-home-report-summary]');
  if (summary) {
    summary.textContent = reportTeaser(report.opening_text);
    summary.hidden = !summary.textContent;
  }
  const link = card.querySelector('[data-home-report-link]');
  if (link) link.href = report.url;
}

function renderPrintCard(card, report) {
  setImage(card, '[data-home-print-image]', report);
  setText(card, '[data-home-print-title]', report.title);
}

export async function initializeHomepageReports(scope = document, random = Math.random, fetchImpl = globalThis.fetch) {
  const source = scope.querySelector('[data-home-report-pool]');
  if (!source) return false;
  let pool;
  let countries;
  let total = Number(source.dataset.homeReportTotal);
  if (!Number.isSafeInteger(total) || total < 0) total = null;
  try { pool = JSON.parse(source.textContent); } catch { return false; }
  if (!Array.isArray(pool) || !pool.length) {
    try {
      const loaded = await loadHomepageReportPool(source.dataset.homeReportIndexUrl, fetchImpl);
      pool = loaded.reports;
      total = loaded.total;
      countries = loaded.countries;
    } catch { return false; }
  }
  renderReportCount(scope, total);
  const countryCount = new Set(countries || pool.flatMap((report) => String(report.country || '').trim().split(/\s*\/\s*/)).filter(Boolean)).size;
  scope.querySelectorAll('[data-home-country-count]').forEach((element) => {
    element.textContent = `${countryCount} COUNTRIES`;
  });
  const selected = pickNewestReports(pool, 4);
  const prints = pickRandomReports(pool, 9, random);
  if (!selected.length) return false;

  scope.querySelectorAll('[data-home-report-hero-image]').forEach((image) => {
    image.src = selected[0].image;
    image.removeAttribute?.('srcset');
    image.alt = selected[0].imageAlt || selected[0].title;
  });
  scope.querySelectorAll('[data-home-report-card]').forEach((card, index) => {
    if (selected[index]) renderEvidenceCard(card, selected[index]);
    else card.hidden = true;
  });
  scope.querySelectorAll('[data-home-print-placeholder]').forEach((card, index) => {
    if (prints[index + 5]) renderPrintCard(card, prints[index + 5]);
  });
  return true;
}

if (typeof document !== 'undefined') void initializeHomepageReports();
